import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { throwError, of } from 'rxjs';
import { catchError, map, retryWhen, delay, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class WaTaxService {

  constructor(private http: HttpClient) { }

  lookupWaTax(address: string, zip: number, city?: string) {
    const cityString = city || '';
    return this.http.get(`https://webgis.dor.wa.gov/webapi/AddressRates.aspx?output=xml&addr=${address}&city=${cityString}&zip=${zip}`, {
      responseType: 'text' // Get the response as text (XML)
    }).pipe(
      map(response => this.parseXmlResponse(response)),
      retryWhen(errors =>
        errors.pipe(
          // Check if the error meets the criteria for a retry
          map(err => {
            if (err.rate === -1 || err.incompatible) {
              throw err;
            }
            return err;
          }),
          delay(1000), // Wait for 1 second before retrying
          take(3) // Retry up to 3 times
        )
      ),
      catchError(error => {
        // Handle any other errors or after retries are exhausted
        console.error('Error fetching WA Tax:', error);
        return throwError(() => new Error('Failed to fetch WA Tax'));
      })
    );
  }

  private parseXmlResponse(xmlStr: string) {
    const parser = new DOMParser();
    const xml = parser.parseFromString(xmlStr, "application/xml");
    const response = xml.querySelector('response');
    if (!response) {
      throw { incompatible: true };
    }
    const rate = response.getAttribute('rate');
    if (rate === '-1') {
      throw { rate: -1 };
    }
    return {
      loccode: Number(response.getAttribute('loccode')) || null,
      localrate: Number(response.getAttribute('localrate')) || null,
      rate: Number(rate)*100,
      code: Number(response.getAttribute('code')) || null,
      addressline: this.parseAddressLine(xml.querySelector('addressline')) || '',
      rateDetails: this.parseRateDetails(xml.querySelector('rate')) || ''
    };
  }

  private parseAddressLine(addressLineElement: Element | null) {
    if (!addressLineElement) return null;
    return {
      code: addressLineElement.getAttribute('code'),
      street: addressLineElement.getAttribute('street'),
      househigh: addressLineElement.getAttribute('househigh'),
      houselow: addressLineElement.getAttribute('houselow'),
      evenodd: addressLineElement.getAttribute('evenodd'),
      state: addressLineElement.getAttribute('state'),
      zip: addressLineElement.getAttribute('zip'),
      plus4: addressLineElement.getAttribute('plus4'),
      period: addressLineElement.getAttribute('period'),
      rta: addressLineElement.getAttribute('rta'),
      ptba: addressLineElement.getAttribute('ptba'),
      cez: addressLineElement.getAttribute('cez'),
    };
  }

  private parseRateDetails(rateElement: Element | null) {
    if (!rateElement) return null;
    return {
      name: rateElement.getAttribute('name'),
      code: rateElement.getAttribute('code'),
      staterate: rateElement.getAttribute('staterate'),
      localrate: rateElement.getAttribute('localrate'),
    };
  }
}